import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGetBenefitComparisonChecksQuery } from "store/queries/benefitComparison";
import {
  IComparisonCheck,
  IComparisonChecksRequest,
} from "types/BenefitComparison/BenefitComparison";

import {
  CHECK_STATUS_LABELS,
  CHECK_STATUS_SEVERITY,
  confidenceBand,
  formatComparisonValue,
  NEEDS_A_VERDICT,
} from "./benefitComparisonWording";
import { BenefitVerdictPanel } from "./BenefitVerdictPanel";
import { PverifyRequestLink, RemitClaimModal } from "./RemitAccuracyLinks";

const PAGE_SIZE = 50;

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-color-secondary">{children}</span>;
}

const requestBody = (row: IComparisonCheck) => (
  <PverifyRequestLink requestId={row.pverifyRequestId} />
);

const orderBody = (row: IComparisonCheck) =>
  row.orderId ? (
    <Link
      to={`/orders/${row.orderId}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Estimate
    </Link>
  ) : (
    <span className="text-color-secondary">—</span>
  );

/** A check with neither a remit nor a human verdict — the rows a reviewer is ASKED to look at. */
export const needsAVerdict = (row: IComparisonCheck) =>
  row.remit.value === null && row.verdict === null;

/**
 * CLE-2820 — the checks in one cluster: legacy, engine and AI side by side, then the two sources
 * that can say the engine was WRONG (the remit and the human verdict), then the check's Status.
 *
 * 🚨 CLICKING A ROW SELECTS IT AND NOTHING ELSE. It loads that check into the response panel above,
 * which is the whole job of a row click — reading the response is how you decide whether a review is
 * even warranted. It used to open the verdict form too, so scrolling the table shoved a form under
 * every row you touched on the way past.
 *
 * 🚨 THE REVIEW BUTTON IS THE ONLY THING THAT OPENS THE FORM, AND IT TOGGLES. Opening is a decision,
 * so it takes a deliberate click, and the same click shuts it again. There is no expander chevron:
 * a second way in would be a second rule for when the form appears.
 *
 * One row's form is open at a time, shutting it leaves the row selected so the panel above keeps its
 * check, and there are NO KEYBOARD SHORTCUTS — no digit keys, no J/K, no global keydown handler.
 *
 * 🚨 THE REMIT VALUE IS A LINK to its claim. This is the one place somebody can CHECK a remit rather
 * than trust it, which matters because a conflict means the reviewer overruled the remit, and the
 * reader should be able to see what the payer paid.
 */
export function BenefitComparisonChecks({
  request,
  selected,
  onSelect,
  showAccount = false,
}: {
  request: IComparisonChecksRequest;
  selected: string | null;
  onSelect: (pverifyResponseId: string) => void;
  showAccount?: boolean;
}) {
  const [offset, setOffset] = useState(0);
  const [claimId, setClaimId] = useState<string | null>(null);
  /**
   * 🚨 EXPANSION IS ITS OWN STATE, NOT A MIRROR OF THE SELECTION, and only the Review button moves
   * it. Selecting a row must NOT open a form, and closing a form must NOT empty the response panel
   * above — so the row stays selected while its form is shut.
   */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const requestKey = JSON.stringify(request);
  useEffect(() => setOffset(0), [requestKey]);
  const { data, isFetching } = useGetBenefitComparisonChecksQuery({
    ...request,
    limit: PAGE_SIZE,
    offset,
  });
  const page = data?.data;
  const rows = useMemo(() => page?.rows ?? [], [page]);

  // A new cluster, or another page of one, starts with no form open rather than reopening whatever
  // now sits in the same position.
  useEffect(() => setExpandedId(null), [requestKey, offset]);
  /*
   * 🚨 SELECTED ON ARRIVAL, NOT OPEN. The response panel above must never be empty; the verdict form
   * must never appear without being asked for. This used to do both, so every cluster opened with a
   * form nobody had requested under its first row.
   */
  useEffect(() => {
    if (!selected && rows.length > 0) onSelect(rows[0].pverifyResponseId);
  }, [rows, selected, onSelect]);

  const current =
    rows.find((row) => row.pverifyResponseId === selected) ?? rows[0] ?? null;

  /**
   * 🚨 REVIEW TOGGLES, and it is the ONLY thing that opens the form. Clicking it on the open row
   * shuts it — the same button, the same place, so there is nothing to hunt for to get the table
   * back. It selects as well, because reviewing a check you cannot see above would be absurd.
   */
  const toggleReview = (pverifyResponseId: string) => {
    setExpandedId((open) =>
      open === pverifyResponseId ? null : pverifyResponseId
    );
    onSelect(pverifyResponseId);
  };
  /** 🚨 "N/A", not "no value": in a grid of figures a phrase reads as a value of its own. */
  const value = (amount: number | null) =>
    amount === null ? (
      <span className="text-color-secondary">N/A</span>
    ) : (
      formatComparisonValue(amount, request.benefit)
    );

  /**
   * 🚨 THE CONFIDENCE IS A WORD IN BRACKETS, not a decimal after a dot. A reader acts on "is this
   * worth checking", and 0.57 asks them to hold the whole distribution in their head to answer that;
   * the bands are cut where the measured match rate against remits actually breaks.
   */
  const aiBody = (row: IComparisonCheck) => {
    if (row.aiValue === null && row.aiConfidence === null)
      return <span className="text-color-secondary">not asked</span>;
    const band = confidenceBand(row.aiConfidence);
    return (
      <span className="white-space-nowrap">
        {value(row.aiValue)}
        {band && <Muted> ({band})</Muted>}
      </span>
    );
  };

  /** 🚨 A link, not a figure: the one place a remit can be checked rather than trusted. */
  const remitBody = (row: IComparisonCheck) => {
    if (row.remit.value === null)
      return <span className="text-color-secondary">—</span>;
    if (!row.remit.claimId) return <span>{value(row.remit.value)}</span>;
    return (
      <Button
        type="button"
        className="p-button-link p-0 font-semibold"
        label={String(formatComparisonValue(row.remit.value, request.benefit))}
        data-testid="benefitComparison.checks.remitLink"
        onClick={(event) => {
          event.stopPropagation();
          setClaimId(row.remit.claimId);
        }}
      />
    );
  };

  const verdictBody = (row: IComparisonCheck) => {
    if (!row.verdict) return <span className="text-color-secondary">—</span>;
    return (
      <span className="white-space-nowrap">
        {row.verdict.verdict === "VALUE"
          ? value(row.verdict.value)
          : "Can’t tell"}{" "}
        <span className="text-color-secondary">
          {row.verdict.reviewedByName ?? ""}
        </span>
      </span>
    );
  };

  const statusBody = (row: IComparisonCheck) => {
    if (!row.verification)
      return (
        <Tag
          value={NEEDS_A_VERDICT}
          data-testid="benefitComparison.checks.needsVerdict"
        />
      );
    return (
      <span className="flex flex-wrap gap-1">
        <Tag
          severity={CHECK_STATUS_SEVERITY[row.verification.status]}
          value={CHECK_STATUS_LABELS[row.verification.status]}
        />
        {/* 🚨 A remit and a verdict disagree. Flagged, both kept, never resolved. */}
        {row.remitConflict && (
          <Tag
            severity="danger"
            value="Conflict"
            data-testid="benefitComparison.checks.remitConflict"
          />
        )}
      </span>
    );
  };

  /**
   * 🚨 ON EVERY ROW, NOT ONLY THE ONES THAT NEED ONE. It used to appear only where neither a remit
   * nor a verdict existed, which made the rows a reviewer most wants to challenge — a `Matching`
   * row, where the engine agrees with a remit — the exact rows they could not say anything about.
   * A remit is evidence, not proof (the screen links every remit to its claim precisely so somebody
   * can check it), and a human who has read the response must be able to record what it should have
   * said whatever else agrees. `needsAVerdict` still marks which rows are ASKED for, via the Status
   * column's "Needs human input" tag — it no longer decides who is ALLOWED.
   *
   * On a row that already has a verdict the panel opens showing it, with its own Change control, so
   * this button never silently discards a recorded answer.
   */
  const reviewBody = (row: IComparisonCheck) => (
    <Button
      type="button"
      label="Review"
      className="p-button-outlined p-button-sm"
      data-testid="benefitComparison.checks.review"
      onClick={(event) => {
        event.stopPropagation();
        toggleReview(row.pverifyResponseId);
      }}
    />
  );

  /**
   * 🚨 THE VERDICT FORM LIVES INSIDE THE ROW. It used to sit under the table, which put it a whole
   * page below the row it belonged to once a cluster ran to fifty checks — you clicked a row near
   * the top and the form appeared off-screen. Expansion mirrors selection: one row open at a time,
   * the same row whose response is loaded in the panel above.
   */
  const rowExpansion = (row: IComparisonCheck) => (
    <div className="p-3" data-testid="benefitComparison.checks.panel">
      <div className="flex flex-wrap align-items-center gap-3 mb-3 text-sm">
        <span>{row.checkedAt.slice(0, 10)}</span>
        <PverifyRequestLink requestId={row.pverifyRequestId} />
        <span>
          Engine <strong>{value(row.engineValue)}</strong>
        </span>
        <span>
          Legacy <strong>{value(row.legacyValue)}</strong>
        </span>
        <span>
          AI <strong>{value(row.aiValue)}</strong>
          {confidenceBand(row.aiConfidence) && (
            <Muted> ({confidenceBand(row.aiConfidence)})</Muted>
          )}
        </span>
        {/* 🚨 The two sources that can say the engine was wrong, beside the question. */}
        <span
          className={row.remit.value === null ? "text-color-secondary" : ""}
        >
          Remit{" "}
          {row.remit.value === null ? (
            "N/A"
          ) : (
            <strong>{value(row.remit.value)}</strong>
          )}
        </span>
        <span className={row.verdict === null ? "text-color-secondary" : ""}>
          Human input{" "}
          {row.verdict === null ? (
            "N/A"
          ) : (
            <strong>
              {row.verdict.verdict === "VALUE"
                ? value(row.verdict.value)
                : "Can’t tell"}
            </strong>
          )}
        </span>
      </div>
      <BenefitVerdictPanel
        pverifyResponseId={row.pverifyResponseId}
        benefit={row.benefit}
        options={row.verdictOptions}
        verdict={row.verdict}
        remitValue={row.remit.value}
        remitConflict={row.remitConflict}
        agreement={row.verdictAgreement}
        /*
         * 🚨 CANCEL SHUTS THE ROW, it does not just empty the fields. Clearing a form that stays
         * open is indistinguishable from the form failing to do anything, which is exactly how it
         * read — the panel has its own reset, and this is what makes Cancel mean "never mind".
         */
        onCancel={() => setExpandedId(null)}
      />
    </div>
  );

  return (
    <>
      <DataTable
        value={rows}
        dataKey="pverifyResponseId"
        selectionMode="single"
        selection={current}
        /* 🚨 SELECT ONLY. Opening the form is the Review button's job and nothing else's. */
        onRowClick={(event) =>
          onSelect((event.data as IComparisonCheck).pverifyResponseId)
        }
        expandedRows={expandedId ? { [expandedId]: true } : {}}
        rowExpansionTemplate={rowExpansion}
        lazy
        paginator
        rows={PAGE_SIZE}
        first={offset}
        totalRecords={page?.total ?? 0}
        onPage={(event) => setOffset(event.first)}
        loading={isFetching}
        emptyMessage="No checks."
        data-testid="benefitComparison.checks"
      >
        {/*
         * 🚨 NO EXPANDER CHEVRON. It was a second way to open the verdict form, competing with the
         * Review button for the same job — and on a row where a review was not being asked for it
         * opened a form the row had no button to close.
         */}
        <Column
          header="Checked"
          body={(row: IComparisonCheck) => row.checkedAt.slice(0, 10)}
        />
        {showAccount && <Column field="accountName" header="Account" />}
        <Column header="pVerify Request" body={requestBody} />
        <Column header="Estimate" body={orderBody} />
        <Column
          header="Engine"
          body={(row: IComparisonCheck) => value(row.engineValue)}
        />
        <Column
          header="Legacy"
          body={(row: IComparisonCheck) => value(row.legacyValue)}
        />
        <Column header="AI" body={aiBody} />
        <Column header="Remit" body={remitBody} />
        <Column header="Human input" body={verdictBody} />
        <Column header="Status" body={statusBody} />
        <Column header="" body={reviewBody} />
      </DataTable>

      {/* 🚨 The remit, opened rather than trusted — the same modal the Remit accuracy tab uses. */}
      {claimId && current && (
        <RemitClaimModal
          claimId={claimId}
          accountId={current.accountId}
          onHide={() => setClaimId(null)}
        />
      )}
    </>
  );
}
